export function updateSearchParam(
  prev: URLSearchParams,
  key: string,
  value: string | null
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value === null) next.delete(key);
  else next.set(key, value);
  return next;
}

// ──────────────────────────────────────────────
// ?propertyIds / ?requestId helpers — shared by GPC's PricingRequestsTable
// and GPRT's GprtRequestsTable, whose request-detail drawers both deep-link
// via the same URL shape: one ?propertyIds list (never a separate, redundant
// ?propertyId) plus ?requestId.
// ──────────────────────────────────────────────

export const parsePropertyIdsParam = (params: URLSearchParams): string[] =>
  params.get("propertyIds")?.split(",").filter(Boolean) ?? [];

/** Unions `propertyId` into the existing `?propertyIds` list — never drops
 * what's already there, so opening a request while multiple properties are
 * selected doesn't narrow that selection. */
export const foldPropertyIntoIds = (
  params: URLSearchParams,
  propertyId: string
): URLSearchParams => {
  // Defense-in-depth: every real caller sources propertyId from a required,
  // non-nullable field on an already-fetched row (or from a pre-guarded
  // legacy ?propertyId param) — this just avoids appending an empty
  // segment to ?propertyIds (e.g. "prop1,prop2,") if that ever breaks.
  if (!propertyId) return params;
  const current = parsePropertyIdsParam(params);
  const withProperty = current.includes(propertyId)
    ? current
    : [...current, propertyId];
  return updateSearchParam(params, "propertyIds", withProperty.join(","));
};

/** Builds the params for opening a request: propertyId folded into
 * ?propertyIds (never a separate ?propertyId), ?propertyIds set before
 * ?requestId so a freshly-built link orders the same way the page's own URL
 * already does. */
export const openRequestInParams = (
  params: URLSearchParams,
  requestId: string,
  propertyId: string
): URLSearchParams =>
  updateSearchParam(
    foldPropertyIntoIds(params, propertyId),
    "requestId",
    requestId
  );

/** Legacy inbound links (e.g. notification emails) may still carry the old
 * singular ?propertyId — folds it into ?propertyIds and drops the old key.
 * Returns null (no-op) when there's nothing legacy to normalize. */
export const normalizeLegacyPropertyId = (
  params: URLSearchParams
): URLSearchParams | null => {
  const legacyPropertyId = params.get("propertyId");
  if (!legacyPropertyId) return null;
  return updateSearchParam(
    foldPropertyIntoIds(params, legacyPropertyId),
    "propertyId",
    null
  );
};

/** Which single property a deep-linked request belongs to: unambiguous when
 * exactly one property is in scope; otherwise resolved from a row the caller
 * has already matched against the requestId (e.g. via `rows.find(...)`) —
 * present once the request was opened from a click, or once the list finishes
 * loading for a deep link. */
export const resolveDrawerPropertyId = (
  params: URLSearchParams,
  matchingRow: { propertyId: string } | null | undefined
): string | null => {
  const ids = parsePropertyIdsParam(params);
  if (ids.length === 1) return ids[0];
  return matchingRow?.propertyId ?? null;
};

/** Shared by both drawers' Refresh tooltip when a request's property
 * couldn't be resolved (see `resolveDrawerBlockedMessage`). */
export const DRAWER_BLOCKED_TOOLTIP = "Check the URL and try again";

/** Message shown in place of a request-detail drawer's content when its
 * `propertyId` couldn't be resolved from the URL/loaded rows — the fetch
 * would otherwise no-op (GPC) or hit the API with a malformed path (GPRT),
 * leaving the panel blank with no feedback. Shared by GPC's
 * PricingRequestsTable and GPRT's GprtRequestsTable, whose drawers both
 * degrade the same way once `hasFetchedOnce` and not still `loading`/
 * `loadingMore`. Returns null while genuinely still loading, since
 * resolution can still succeed once more rows land. */
export const resolveDrawerBlockedMessage = ({
  drawerPropertyId,
  hasFetchedOnce,
  loading,
  loadingMore,
  moreAvailable,
}: {
  drawerPropertyId: string | null;
  hasFetchedOnce: boolean;
  loading: boolean;
  loadingMore: boolean;
  moreAvailable: boolean;
}): string | null => {
  if (drawerPropertyId || !hasFetchedOnce || loading || loadingMore)
    return null;
  return moreAvailable
    ? "This request isn't in the requests loaded so far for your selected properties — try Load more below, or check the URL."
    : "This request doesn't match any of your currently selected properties. Check the URL and try again.";
};
